import React, { useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import clsx from 'clsx';
import { APP_COLORS } from '../../config/colors';
import type { BarChartProps, ChartData, ChartOptions } from './BarChart.types';

// Plugin do rysowania przerywanych linii siatki
const dashedGridPlugin = {
  id: 'dashedGrid',
  beforeDraw(chart: {
    ctx: CanvasRenderingContext2D;
    chartArea: { left: number; right: number };
    scales: {
      y: { ticks: unknown[]; getPixelForTick: (index: number) => number };
    };
  }) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.y) return;

    const yScale = scales.y;
    ctx.save();

    // Ustawienia linii przerywanych
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#EAECF6';
    ctx.lineWidth = 1;

    // Rysuj linie poziome
    yScale.ticks.forEach((_tick: unknown, index: number) => {
      if (index === 0) return; // Pomiń pierwszą linię (dolną)

      const y = yScale.getPixelForTick(index);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
    });

    ctx.restore();
  },
};

// Plugin do wyświetlania etykiet nad słupkami
const topLabelsPlugin = {
  id: 'topLabels',
  afterDatasetsDraw(
    chart: {
      ctx: CanvasRenderingContext2D;
      data: { datasets: Array<{ topLabels?: string[] }> };
      getDatasetMeta: (index: number) => {
        data: Array<{ x: number; y: number }>;
      };
    },
    _args: unknown,
    options: { showTopLabels?: boolean },
  ) {
    const { ctx, data } = chart;

    if (!options.showTopLabels || !data.datasets[0]?.topLabels) return;

    ctx.save();

    data.datasets[0].topLabels.forEach((label: string, index: number) => {
      if (!label) return;

      const meta = chart.getDatasetMeta(0);
      const bar = meta.data[index];

      if (bar) {
        const x = bar.x;
        const y = bar.y - 25; // 25px nad słupkiem dla większego odstępu

        // Konfiguracja fontu zgodna z Tailwind (text-xs font-medium uppercase)
        ctx.font = '600 12px Inter, sans-serif'; // font-medium = 600, text-xs = 12px
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Obliczenie wymiarów etykiety zgodnie z designem
        const upperCaseLabel = label.toUpperCase(); // uppercase
        const textMetrics = ctx.measureText(upperCaseLabel);
        const textWidth = textMetrics.width;

        // Padding zgodny z px-3.5 py-1.5 (14px horizontal, 6px vertical)
        const paddingX = 14; // px-3.5 = 14px
        const paddingY = 6; // py-1.5 = 6px

        const labelWidth = textWidth + paddingX * 2;
        const labelHeight = 12 + paddingY * 2; // 12px to wysokość tekstu + padding

        const labelX = x - labelWidth / 2;
        const labelY = y - labelHeight / 2;

        // Tło etykiety - rounded-lg bg-[#20262a]
        ctx.fillStyle = '#20262A';

        // Rysowanie zaokrąglonego prostokąta (rounded-lg = 8px border-radius)
        const borderRadius = 8;
        ctx.beginPath();

        // Fallback dla starszych przeglądarek bez roundRect
        if (ctx.roundRect) {
          ctx.roundRect(labelX, labelY, labelWidth, labelHeight, borderRadius);
        } else {
          // Fallback - zwykły prostokąt
          ctx.rect(labelX, labelY, labelWidth, labelHeight);
        }
        ctx.fill();

        // Tekst etykiety - text-white font-medium text-xs uppercase
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(upperCaseLabel, x, y);
      }
    });

    ctx.restore();
  },
};

// Rejestracja komponentów Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  dashedGridPlugin,
  topLabelsPlugin,
);

/**
 * Komponent wykresu słupkowego z wykorzystaniem Chart.js
 * Obsługuje etykiety nad słupkami zgodne z designem Tailwind CSS
 * Słupki mają zaokrąglenie 50px od góry i dołu oraz szerokość 40px
 * Wykres ma 50px padding od góry, aby etykiety nie były przycinane
 *
 * @example
 * ```tsx
 * <BarChart
 *   data={[
 *     { label: '2014', value: 188413, topLabel: '188 413' },
 *     { label: '2025', value: 45255, topLabel: '45 255' }
 *   ]}
 *   title="Wartość pojazdu w latach"
 *   height={400}
 *   showTopLabels={true}
 * />
 * ```
 */
export const BarChart: React.FC<BarChartProps> = ({
  data,
  title,
  height = 500,
  width,
  showLegend = false,
  showGrid = true,
  showValues = true,
  showLegendBelow,
  showTopLabels = true,
  valueFormat = 'PLN',
  customFormatter,
  defaultColor = APP_COLORS.primary.DEFAULT,
  className,
  responsive = true,
  minBarHeight = 20,
  maxBarWidth,
  onBarClick,
}) => {
  /**
   * Określa czy wyświetlać legendę pod wykresem
   * showLegendBelow ma priorytet nad showValues
   */
  const shouldShowLegendBelow =
    showLegendBelow !== undefined ? showLegendBelow : showValues;

  /**
   * Formatuje wartość zgodnie z wybranym formatem
   */
  const formatValue = useCallback(
    (value: number): string => {
      if (customFormatter) {
        return customFormatter(value);
      }

      switch (valueFormat) {
        case 'PLN':
          return `${value.toLocaleString('pl-PL')} PLN`;
        case 'K':
          return value >= 1000
            ? `${Math.round(value / 1000)}k`
            : value.toString();
        default:
          return value.toString();
      }
    },
    [valueFormat, customFormatter],
  );

  /**
   * Przygotowuje dane dla Chart.js
   */
  const chartData: ChartData = useMemo(() => {
    const labels = data.map((item) => item.label);
    const values = data.map((item) => item.value);
    const colors = data.map((item) => item.color || defaultColor);
    const topLabels = data.map((item) => item.topLabel || '');

    return {
      labels,
      datasets: [
        {
          label: title || 'Wartość',
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((color) => color),
          borderWidth: 0,
          borderRadius: {
            topLeft: 50, // zaokrąglenie góra-lewo: 50px
            topRight: 50, // zaokrąglenie góra-prawo: 50px
            bottomLeft: 50, // zaokrąglenie dół-lewo: 50px
            bottomRight: 50, // zaokrąglenie dół-prawo: 50px
          },
          barThickness: 40, // szerokość: 40px
          maxBarThickness: maxBarWidth || 40, // fallback na 40px jeśli nie podano
          topLabels: showTopLabels ? topLabels : [],
        },
      ],
    };
  }, [data, defaultColor, title, maxBarWidth, showTopLabels]);

  /**
   * Konfiguracja opcji Chart.js
   */
  const options: ChartOptions = useMemo(
    () => ({
      responsive,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 40,
          bottom: 0,
          left: 0,
          right: 0,
        },
      },
      elements: {
        bar: {
          borderRadius: 50,
        },
      },
      plugins: {
        legend: {
          display: showLegend,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: { parsed: { y: number } }) => {
              const value = context.parsed.y;
              return formatValue(value);
            },
          },
        },
        topLabels: {
          showTopLabels,
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false, // Wyłączenie linii pionowych
          },
        },
        y: {
          display: true,
          grid: {
            display: showGrid,
            tickBorderDash: [4, 4],
            tickBorderDashOffset: 4,
            color: '#EAECF6', // Kolor zgodny z SVG
            // lineWidth: 1, // Grubość linii siatki
            // borderDash: [4, 4], // Linie przerywane
            // borderDashOffset: 0, // Offset linii przerywanych
            // drawOnChartArea: true, // Rysuj linie na obszarze wykresu
            // drawTicks: false, // Nie rysuj znaczników na osi
          },
          border: {
            display: false, // Wyłącz ramkę osi
          },
          ticks: {
            callback: (value: number | string) => {
              const numValue =
                typeof value === 'number'
                  ? value
                  : parseFloat(value.toString());
              return formatValue(numValue);
            },
          },
        },
      },
      onClick: onBarClick
        ? (_event: MouseEvent, elements: { index: number }[]) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              const dataPoint = data[index];
              if (dataPoint) {
                onBarClick(dataPoint, index);
              }
            }
          }
        : undefined,
    }),
    [
      responsive,
      showLegend,
      showGrid,
      showTopLabels,
      formatValue,
      onBarClick,
      data,
    ],
  );

  /**
   * Oblicza dynamiczną wysokość na podstawie ilości danych
   */
  const dynamicHeight = useMemo(() => {
    if (!responsive) return height;

    const baseHeight = 300;
    const itemHeight = 40;
    const calculatedHeight = Math.max(
      baseHeight,
      data.length * itemHeight + 100,
    );

    return Math.min(calculatedHeight, height);
  }, [data.length, height, responsive]);

  const containerStyles = useMemo(
    () => ({
      height: dynamicHeight,
      width: width || '100%',
      minHeight: minBarHeight * data.length + 100,
    }),
    [dynamicHeight, width, minBarHeight, data.length],
  );

  return (
    <div
      className={clsx(
        'w-full rounded-card border border-grayBorder p-6',
        className,
      )}
    >
      {title && (
        <h3 className="mb-4 text-lg font-semibold text-text-primary">
          {title}
        </h3>
      )}

      <div className="relative" style={containerStyles}>
        <Bar data={chartData} options={options as never} />
      </div>

      {shouldShowLegendBelow && (
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-text-muted">
          {data.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className="flex items-center gap-2"
            >
              <div
                className="h-3 w-3 rounded-sm"
                style={{ backgroundColor: item.color || defaultColor }}
              />
              <span>
                {item.label}: {formatValue(item.value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarChart;
